// Console Kafka pour les services managés Kafka.
//
// Même modèle que StudioSection : à l'ouverture, POST /kafka-console/.../session
// démarre un pod BFF éphémère dans le namespace du tenant ; à la fermeture,
// DELETE (idempotent — le BFF s'éteint aussi seul après 15 min d'inactivité).
//
// Tous les appels passent par POST /proxy avec {method, path} dans le corps :
// le backend valide le chemin contre une whitelist. Ne pas contourner en
// appelant le pod directement, il n'est pas exposé.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import Logger from '../utils/logger';
import { showErrorToast, showSuccessToast } from '../utils/toastUtils';
import type {
  KafkaGroupLagPartition,
  KafkaMessage,
  KafkaSessionResponse,
  KafkaTopic,
} from '../types/api';

interface KafkaConsoleSectionProps {
  deploymentId: string;
}

type Panel = 'topics' | 'groups' | 'messages';

const KafkaConsoleSection: React.FC<KafkaConsoleSectionProps> = ({ deploymentId }) => {
  // (useTranslation as any) : neutralise TS2589 (react-i18next 14) a la source
  const { t } = (useTranslation as any)('deployments');

  const [isOpen, setIsOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [session, setSession] = useState<KafkaSessionResponse | null>(null);
  const [panel, setPanel] = useState<Panel>('topics');
  const [loading, setLoading] = useState(false);

  const [topics, setTopics] = useState<KafkaTopic[]>([]);
  const [groups, setGroups] = useState<Array<{ groupId: string; state: string | null }>>([]);
  const [lag, setLag] = useState<KafkaGroupLagPartition[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [messages, setMessages] = useState<KafkaMessage[]>([]);

  const [newTopic, setNewTopic] = useState({ name: '', partitions: 1, replicationFactor: 1 });
  const [produceValue, setProduceValue] = useState('');

  // -- chargements -----------------------------------------------------------

  const loadTopics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.kafkaProxy<{ topics: KafkaTopic[] }>(
        deploymentId, 'GET', '/topics'
      );
      setTopics(data.topics || []);
    } catch (err) {
      showErrorToast(err, t('kafka.errors.loadTopics') as string, 'kafka-topics');
    } finally {
      setLoading(false);
    }
  }, [deploymentId, t]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.kafkaProxy<{ groups: Array<{ groupId: string; state: string | null }> }>(
        deploymentId, 'GET', '/groups'
      );
      setGroups(data.groups || []);
      setLag([]);
    } catch (err) {
      showErrorToast(err, t('kafka.errors.loadGroups') as string, 'kafka-groups');
    } finally {
      setLoading(false);
    }
  }, [deploymentId, t]);

  const loadLag = useCallback(async (groupId: string) => {
    setLoading(true);
    try {
      const data = await apiService.kafkaProxy<{ partitions: KafkaGroupLagPartition[] }>(
        deploymentId, 'GET', `/groups/${encodeURIComponent(groupId)}/lag`
      );
      setLag(data.partitions || []);
    } catch (err) {
      showErrorToast(err, t('kafka.errors.loadLag') as string, 'kafka-lag');
    } finally {
      setLoading(false);
    }
  }, [deploymentId, t]);

  const loadMessages = useCallback(async (topic: string) => {
    setLoading(true);
    setSelectedTopic(topic);
    setPanel('messages');
    try {
      const data = await apiService.kafkaProxy<{ messages: KafkaMessage[] }>(
        deploymentId, 'GET', `/topics/${encodeURIComponent(topic)}/messages?limit=20`
      );
      setMessages(data.messages || []);
    } catch (err) {
      showErrorToast(err, t('kafka.errors.loadMessages') as string, 'kafka-messages');
    } finally {
      setLoading(false);
    }
  }, [deploymentId, t]);

  // -- session ---------------------------------------------------------------

  const openConsole = useCallback(async () => {
    setOpening(true);
    try {
      const s = await apiService.openKafkaSession(deploymentId);
      setSession(s);
      setIsOpen(true);
      await loadTopics();
    } catch (err) {
      showErrorToast(err, t('kafka.errors.openSession') as string, 'kafka-session');
    } finally {
      setOpening(false);
    }
  }, [deploymentId, loadTopics, t]);

  const closeConsole = useCallback(async () => {
    setIsOpen(false);
    setSession(null);
    setTopics([]); setGroups([]); setLag([]); setMessages([]); setSelectedTopic(null);
    try {
      await apiService.closeKafkaSession(deploymentId);
    } catch (err) {
      // La fermeture est best-effort : le BFF s'éteint seul après 15 min.
      Logger.error('Kafka: fermeture de session', err);
    }
  }, [deploymentId]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) closeConsole(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, closeConsole]);

  // -- actions ---------------------------------------------------------------

  const createTopic = async () => {
    const name = newTopic.name.trim();
    if (!name) return;
    try {
      await apiService.kafkaProxy(deploymentId, 'POST', '/topics', {
        name,
        partitions: Number(newTopic.partitions) || 1,
        replicationFactor: Number(newTopic.replicationFactor) || 1,
      });
      showSuccessToast(t('kafka.topicCreated', { name }) as string);
      setNewTopic({ name: '', partitions: 1, replicationFactor: 1 });
      await loadTopics();
    } catch (err) {
      showErrorToast(err, t('kafka.errors.createTopic') as string, 'kafka-create');
    }
  };

  const deleteTopic = async (name: string) => {
    if (!window.confirm(t('kafka.confirmDeleteTopic', { name }) as string)) return;
    try {
      await apiService.kafkaProxy(deploymentId, 'DELETE', `/topics/${encodeURIComponent(name)}`);
      showSuccessToast(t('kafka.topicDeleted', { name }) as string);
      await loadTopics();
    } catch (err) {
      showErrorToast(err, t('kafka.errors.deleteTopic') as string, 'kafka-delete');
    }
  };

  const produce = async () => {
    if (!selectedTopic || !produceValue) return;
    try {
      await apiService.kafkaProxy(
        deploymentId, 'POST',
        `/topics/${encodeURIComponent(selectedTopic)}/messages`,
        { value: produceValue }
      );
      showSuccessToast(t('kafka.messageSent') as string);
      setProduceValue('');
      await loadMessages(selectedTopic);
    } catch (err) {
      showErrorToast(err, t('kafka.errors.produce') as string, 'kafka-produce');
    }
  };

  // -- rendu -----------------------------------------------------------------

  if (!isOpen) {
    return (
      <button
        onClick={openConsole}
        disabled={opening}
        className="w-full flex items-center justify-between p-4 bg-surface-2 rounded-lg border border-border-1 hover:border-accent-500 transition-colors group disabled:opacity-60"
      >
        <div className="flex items-center space-x-3">
          <ArchiveBoxIcon className="h-6 w-6 text-text-tertiary group-hover:text-accent-500 transition-colors" />
          <div className="text-left">
            <p className="text-sm font-medium text-text-primary">{t('kafka.title')}</p>
            <p className="text-xs text-text-tertiary">{t('kafka.subtitle')}</p>
          </div>
        </div>
        {opening && <ArrowPathIcon className="h-5 w-5 text-text-tertiary animate-spin" />}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="flex items-center justify-center min-h-screen">
        <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={closeConsole} />
        <div className="relative bg-surface-1 rounded-xl shadow-2xl w-[95vw] h-[92vh] flex flex-col">

          <div className="flex items-center justify-between px-5 py-3 border-b border-border-1">
            <div className="flex items-center space-x-3">
              <ArchiveBoxIcon className="h-5 w-5 text-accent-500" />
              <span className="text-sm font-medium text-text-primary">{t('kafka.title')}</span>
              {session && (
                <span className="text-xs font-mono text-text-tertiary">
                  {session.bootstrap} · {session.username}
                </span>
              )}
            </div>
            <button onClick={closeConsole} className="text-text-tertiary hover:text-text-primary">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex space-x-2 px-5 py-2 border-b border-border-1">
            <button
              onClick={() => { setPanel('topics'); loadTopics(); }}
              className={`px-3 py-1 text-xs rounded ${panel === 'topics' ? 'bg-accent-500 text-white' : 'text-text-secondary hover:text-text-primary'}`}
            >{t('kafka.tabs.topics')}</button>
            <button
              onClick={() => { setPanel('groups'); loadGroups(); }}
              className={`px-3 py-1 text-xs rounded ${panel === 'groups' ? 'bg-accent-500 text-white' : 'text-text-secondary hover:text-text-primary'}`}
            >{t('kafka.tabs.groups')}</button>
          </div>

          <div className="flex-1 overflow-auto p-5">
            {loading && <p className="text-xs text-text-tertiary mb-3">{t('kafka.loading')}</p>}

            {panel === 'topics' && (
              <>
                <div className="flex flex-wrap gap-2 mb-4 items-end">
                  <input
                    value={newTopic.name}
                    onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
                    placeholder={t('kafka.newTopicName') as string}
                    className="px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md"
                  />
                  <input
                    type="number" min={1}
                    value={newTopic.partitions}
                    onChange={(e) => setNewTopic({ ...newTopic, partitions: Number(e.target.value) })}
                    className="w-24 px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md"
                    title={t('kafka.partitions') as string}
                  />
                  <button
                    onClick={createTopic}
                    className="px-3 py-2 text-sm bg-accent-500 text-white rounded-md flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" /> {t('kafka.createTopic')}
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-text-tertiary border-b border-border-1">
                    <tr>
                      <th className="text-left py-2">{t('kafka.topic')}</th>
                      <th className="text-left py-2">{t('kafka.partitions')}</th>
                      <th className="text-left py-2">{t('kafka.replication')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {topics.map((tp) => (
                      <tr key={tp.name} className="border-b border-border-1/40">
                        <td className="py-2">
                          <button onClick={() => loadMessages(tp.name)} className="text-accent-500 hover:underline font-mono text-xs">
                            {tp.name}
                          </button>
                        </td>
                        <td className="py-2 text-text-secondary">{tp.partitions}</td>
                        <td className="py-2 text-text-secondary">{tp.replicationFactor ?? '—'}</td>
                        <td className="py-2 text-right">
                          <button onClick={() => deleteTopic(tp.name)} className="text-text-tertiary hover:text-red-500">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {topics.length === 0 && !loading && (
                      <tr><td colSpan={4} className="py-4 text-text-tertiary text-xs">{t('kafka.noTopics')}</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}

            {panel === 'groups' && (
              <>
                <table className="w-full text-sm mb-4">
                  <thead className="text-xs text-text-tertiary border-b border-border-1">
                    <tr><th className="text-left py-2">{t('kafka.group')}</th><th className="text-left py-2">{t('kafka.state')}</th></tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr key={g.groupId} className="border-b border-border-1/40">
                        <td className="py-2">
                          <button onClick={() => loadLag(g.groupId)} className="text-accent-500 hover:underline font-mono text-xs">
                            {g.groupId}
                          </button>
                        </td>
                        <td className="py-2 text-text-secondary">{g.state ?? '—'}</td>
                      </tr>
                    ))}
                    {groups.length === 0 && !loading && (
                      <tr><td colSpan={2} className="py-4 text-text-tertiary text-xs">{t('kafka.noGroups')}</td></tr>
                    )}
                  </tbody>
                </table>
                {lag.length > 0 && (
                  <table className="w-full text-xs font-mono">
                    <thead className="text-text-tertiary border-b border-border-1">
                      <tr>
                        <th className="text-left py-1">{t('kafka.topic')}</th>
                        <th className="text-left py-1">part.</th>
                        <th className="text-left py-1">committed</th>
                        <th className="text-left py-1">end</th>
                        <th className="text-left py-1">lag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lag.map((l, i) => (
                        <tr key={i} className="border-b border-border-1/30">
                          <td className="py-1">{l.topic}</td>
                          <td className="py-1">{l.partition}</td>
                          <td className="py-1">{l.committed}</td>
                          <td className="py-1">{l.logEndOffset ?? '—'}</td>
                          <td className={`py-1 ${l.lag && l.lag > 0 ? 'text-orange-400' : 'text-text-secondary'}`}>
                            {l.lag ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {panel === 'messages' && selectedTopic && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-mono text-text-primary">{selectedTopic}</span>
                  <button onClick={() => loadMessages(selectedTopic)} className="text-xs text-accent-500 flex items-center gap-1">
                    <ArrowPathIcon className="h-3 w-3" /> {t('kafka.refresh')}
                  </button>
                </div>

                {/* Un message Kafka est immuable : ni édition, ni suppression
                    unitaire. Le dire évite la question, surtout pour qui vient
                    de RabbitMQ. */}
                <p className="text-xs text-text-tertiary mb-3 italic">{t('kafka.immutableNote')}</p>

                <div className="flex gap-2 mb-4">
                  <input
                    value={produceValue}
                    onChange={(e) => setProduceValue(e.target.value)}
                    placeholder={t('kafka.producePlaceholder') as string}
                    className="flex-1 px-3 py-2 text-sm border border-border-1 bg-surface-3 text-text-primary rounded-md font-mono"
                  />
                  <button onClick={produce} className="px-3 py-2 text-sm bg-accent-500 text-white rounded-md">
                    {t('kafka.produce')}
                  </button>
                </div>

                <div className="space-y-2">
                  {messages.map((m) => (
                    <div key={m.offset} className="p-2 bg-surface-2 rounded border border-border-1/50">
                      <div className="text-xs text-text-tertiary font-mono mb-1">
                        offset {m.offset}
                        {m.timestamp ? ` · ${new Date(m.timestamp).toLocaleString()}` : ''}
                        {m.key ? ` · key=${m.key}` : ''}
                      </div>
                      <pre className="text-xs text-text-primary whitespace-pre-wrap break-all">{m.value}</pre>
                    </div>
                  ))}
                  {messages.length === 0 && !loading && (
                    <p className="text-xs text-text-tertiary">{t('kafka.noMessages')}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KafkaConsoleSection;
